/**
 * useCategoryStyles Hook
 *
 * Provides consistent styling for navigation menu categories (expandable sections).
 * Categories are the parent items that contain sub-menu items.
 *
 * Uses theme tokens for colors to support all 4 theme variants.
 */

import { useTheme } from '@mui/material/styles';

/**
 * Category styles hook
 * @param {Object} options
 * @param {boolean} options.isDrawerOpen - Is the navigation drawer open
 * @param {number} options.nestingLevel - 0 = main category, 1 = sub-category
 * @returns {Object} Style objects for category components
 */
export const useCategoryStyles = ({ isDrawerOpen = true, nestingLevel = 0 } = {}) => {
  const theme = useTheme();

  return {
    /**
     * Styles for the category header button
     */
    categoryButton: {
      borderRadius: isDrawerOpen ? '20px' : '12px',
      margin: isDrawerOpen ? theme.spacing(0.375, 1.5) : theme.spacing(0.375, 0.5),
      padding: isDrawerOpen ? theme.spacing(1.25, 2) : theme.spacing(1.25, 0.5),
      paddingLeft: isDrawerOpen ? (nestingLevel === 0 ? theme.spacing(2) : theme.spacing(2 + nestingLevel * 0.5)) : theme.spacing(1.25),
      justifyContent: isDrawerOpen ? 'flex-start' : 'center',
      minHeight: isDrawerOpen ? 'auto' : 44,
      transition: theme.transitions.create(
        ['background-color', 'padding', 'margin', 'border-radius'],
        {
          duration: theme.transitions.duration.short,
        }
      ),
      // Hide text when drawer is closed
      '& .MuiListItemText-root': {
        opacity: isDrawerOpen ? 1 : 0,
        display: isDrawerOpen ? 'block' : 'none',
        transition: theme.transitions.create('opacity', {
          duration: theme.transitions.duration.short,
        }),
      },
      // Compact icon spacing when collapsed
      '& .MuiListItemIcon-root': {
        minWidth: isDrawerOpen ? 40 : 28,
      },
      '&:hover': {
        backgroundColor: theme.palette.background?.navHover || 'rgba(0, 0, 0, 0.04)',
        transform: isDrawerOpen ? 'translateX(2px)' : 'scale(1.02)',
      },
      // Style when category is active (one of its children is selected)
      '&.active-category': {
        '& .MuiListItemIcon-root': {
          color: theme.palette.secondary.main,
        },
      },
    },

    /**
     * Styles for the nested list container (wrapper for sub-items)
     */
    nestedList: {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.02)'
          : 'rgba(0, 0, 0, 0.02)',
      borderRadius: isDrawerOpen ? theme.spacing(1) : theme.spacing(0.5),
      margin: isDrawerOpen ? theme.spacing(0.25, 1) : theme.spacing(0.125, 0.5),
      paddingTop: theme.spacing(0.25),
      paddingBottom: theme.spacing(0.25),
      // When collapsed, make nested items more compact
      ...(!isDrawerOpen && {
        '& .MuiListItemButton-root': {
          padding: theme.spacing(1),
          minHeight: 40,
          justifyContent: 'center',
        },
      }),
    },

    /**
     * Get category icon styles based on active state
     * @param {boolean} isActive - Is this category currently active
     * @returns {Object} Icon style object
     */
    categoryIcon: (isActive) => ({
      color: isActive
        ? theme.palette.secondary.main
        : theme.palette.primary.main,
      transition: theme.transitions.create('color', {
        duration: theme.transitions.duration.short,
      }),
      minWidth: isDrawerOpen ? 40 : 24,
      '& .MuiSvgIcon-root': {
        fontSize: isDrawerOpen ? '1.65rem' : '1.4rem',
      },
    }),

    /**
     * Styles for the expand/collapse icon
     * @param {boolean} isExpanded - Is the category expanded
     * @returns {Object} Icon style object
     */
    expandIcon: (isExpanded) => ({
      color: theme.palette.primary.main,
      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.short,
      }),
    }),

    /**
     * Styles for category text
     */
    categoryText: {
      '& .MuiTypography-root': {
        fontWeight: 500,
      },
    },

    /**
     * Styles for sub-category text (smaller)
     */
    subCategoryText: {
      '& .MuiTypography-root': {
        fontWeight: 400,
        fontSize: '0.85rem',
      },
    },

    /**
     * Collapse transition configuration
     */
    collapseProps: {
      timeout: 250,
      unmountOnExit: true,
    },
  };
};

export default useCategoryStyles;
